import { useState } from "react";
import { Check, Pencil, Plus, Sparkles, X } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { Button } from "@runcheetah/ui";

export function EditableTagCollection({
  items,
  onSave,
  emptyLabel,
  initialVisible = 3,
  addLabel,
  allowRegenerate = false,
  onRegenerate,
  testIdPrefix,
}: {
  items: string[];
  onSave: (next: string[]) => Promise<void> | void;
  emptyLabel: string;
  initialVisible?: number;
  addLabel: string;
  allowRegenerate?: boolean;
  onRegenerate?: () => Promise<void> | void;
  testIdPrefix?: string;
}) {
  const intl = useIntl();
  const [isAdding, setIsAdding] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [expanded, setExpanded] = useState(false);
  const visibleItems = expanded ? items : items.slice(0, initialVisible);
  const hiddenCount = Math.max(0, items.length - initialVisible);

  const addItem = async () => {
    const trimmed = draft.trim();
    if (!trimmed || items.some((item) => item.toLowerCase() === trimmed.toLowerCase())) {
      setDraft("");
      return;
    }

    await onSave([...items, trimmed]);
    setDraft("");
    setIsAdding(false);
    setExpanded(true);
  };

  const startEditing = (item: string) => {
    setIsAdding(false);
    setEditingItem(item);
    setDraft(item);
  };

  const cancelDraft = () => {
    setIsAdding(false);
    setEditingItem(null);
    setDraft("");
  };

  const saveEditing = async () => {
    if (!editingItem) {
      return;
    }

    const trimmed = draft.trim();
    if (
      !trimmed ||
      items.some((item) => item !== editingItem && item.toLowerCase() === trimmed.toLowerCase())
    ) {
      setDraft(editingItem);
      setEditingItem(null);
      return;
    }

    await onSave(items.map((item) => (item === editingItem ? trimmed : item)));
    setEditingItem(null);
    setDraft("");
  };

  const removeItem = async (item: string) => {
    if (editingItem === item) {
      cancelDraft();
    }

    await onSave(items.filter((entry) => entry !== item));
  };

  const toggleAdding = () => {
    if (isAdding) {
      cancelDraft();
      return;
    }

    setEditingItem(null);
    setDraft("");
    setIsAdding(true);
  };

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        {items.length ? (
          <div className="divide-y divide-border/45">
            {visibleItems.map((item) => (
              <div key={item} data-testid={testIdPrefix ? `${testIdPrefix}-${item}` : undefined}>
                {editingItem === item ? (
                  <div className="space-y-3 py-5 pr-1">
                    <input
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void saveEditing();
                        }

                        if (event.key === "Escape") {
                          event.preventDefault();
                          cancelDraft();
                        }
                      }}
                      autoFocus
                      placeholder={intl.formatMessage({
                        id: "brandOnboarding.confirm.addItemPlaceholder",
                        defaultMessage: "Add item",
                      })}
                      className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground outline-none ring-primary/30 placeholder:text-muted-foreground focus:ring-2"
                    />
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                        onClick={cancelDraft}
                      >
                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                      </button>
                      <button
                        type="button"
                        className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-semibold text-background transition hover:bg-foreground/88 disabled:cursor-not-allowed disabled:opacity-45"
                        onClick={() => void saveEditing()}
                        disabled={!draft.trim()}
                      >
                        <Check className="h-3.5 w-3.5" aria-hidden />
                        <FormattedMessage id="common.save" defaultMessage="Save" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="group grid grid-cols-[14px_1fr_auto] items-start gap-3 py-5 first:pt-1 last:pb-1">
                    <span
                      aria-hidden
                      className="mt-3 h-1.5 w-1.5 justify-self-center rounded-full bg-[#C8FF00] shadow-[0_0_0_4px_rgba(13,31,45,0.04)]"
                    />
                    <p className="min-w-0 text-base font-semibold tracking-tight text-foreground">
                      {item}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEditing(item)}
                        aria-label={intl.formatMessage({
                          id: "common.edit",
                          defaultMessage: "Edit",
                        })}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </button>
                      <button
                        type="button"
                        onClick={() => void removeItem(item)}
                        aria-label={intl.formatMessage(
                          {
                            id: "brandOnboarding.confirm.removeItem",
                            defaultMessage: "Remove {item}",
                          },
                          { item },
                        )}
                        className="flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : !isAdding ? (
          <p className="text-sm text-muted-foreground">{emptyLabel}</p>
        ) : null}
        {isAdding ? (
          <div className="space-y-3 py-2 pr-1">
            <input
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  void addItem();
                }

                if (event.key === "Escape") {
                  event.preventDefault();
                  cancelDraft();
                }
              }}
              autoFocus
              placeholder={intl.formatMessage({
                id: "brandOnboarding.confirm.addItemPlaceholder",
                defaultMessage: "Add item",
              })}
              className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm font-semibold text-foreground outline-none ring-primary/30 placeholder:text-muted-foreground focus:ring-2"
            />
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-xs font-semibold text-muted-foreground transition hover:bg-muted hover:text-foreground"
                onClick={cancelDraft}
              >
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </button>
              <button
                type="button"
                className="inline-flex h-8 items-center gap-1.5 rounded-full bg-foreground px-3 text-xs font-semibold text-background transition hover:bg-foreground/88 disabled:cursor-not-allowed disabled:opacity-45"
                onClick={() => void addItem()}
                disabled={!draft.trim()}
              >
                <Check className="h-3.5 w-3.5" aria-hidden />
                <FormattedMessage id="common.save" defaultMessage="Save" />
              </button>
            </div>
          </div>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full border-dashed"
            onClick={toggleAdding}
          >
            {isAdding ? (
              <>
                <X className="mr-1 h-4 w-4" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </>
            ) : (
              <>
                <Plus className="mr-1 h-4 w-4" />
                {addLabel}
              </>
            )}
          </Button>
          {hiddenCount > 0 ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="rounded-full text-muted-foreground"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? (
                <FormattedMessage
                  id="brandOnboarding.confirm.showLess"
                  defaultMessage="Show less"
                />
              ) : (
                <FormattedMessage
                  id="brandOnboarding.confirm.showMoreCountCompact"
                  defaultMessage="+{count} more"
                  values={{ count: hiddenCount }}
                />
              )}
            </Button>
          ) : null}
          {allowRegenerate ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto rounded-full text-muted-foreground"
              onClick={() => void onRegenerate?.()}
            >
              <Sparkles className="mr-1 h-3.5 w-3.5" />
              <FormattedMessage
                id="brandOnboarding.confirm.regenerate"
                defaultMessage="Regenerate"
              />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
